import { useEffect, useRef, useState } from "react";

// Issue 43 established this pattern for the section-name field: a text field kept locally
// uncontrolled so a keystroke updates only this component's own state, not the caller's - which
// matters whenever the caller's state also drives something expensive to re-render (a large
// line-item grid, a draft autosave that JSON.stringify's the whole quote, ...). Edits are pushed
// up on a short pause in typing (debounced) and immediately on blur, so anything watching the
// caller's state still sees edits promptly.
//
// `resetKey` is compared by reference/value across renders (a plain useEffect dependency) and
// re-seeds `draft` from `value` whenever it changes - use it for identity changes that should
// override in-progress local edits (a different record loaded, a field cleared programmatically),
// not for the field's own value ticking on every render.
export function useDebouncedDraft(value: string, onChange: (value: string) => void, resetKey: unknown, delay = 300) {
  const [draft, setDraft] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => { setDraft(value); }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => window.clearTimeout(debounceRef.current), []);

  const onDraftChange = (next: string) => {
    setDraft(next);
    window.clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChange(next), delay);
  };
  const onBlur = () => {
    window.clearTimeout(debounceRef.current);
    onChange(draft);
  };
  return { draft, onDraftChange, onBlur };
}
